/** TanStack Query hooks for courses: list, content hierarchy, enrolment checks, categories, and learning time. */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type {
  Course,
  CourseCategory,
  CourseTag,
  CreateCourseInput,
  Enrolment,
  PaginatedResponse,
} from '@formency/shared';
import type { CourseWithContent, CourseLearningTime, CourseListParams } from '@/shared/types';

// Re-export types so existing consumers keep working
export type {
  CourseWithContent,
  ModuleWithContent,
  TopicWithContent,
  LessonWithContent,
  CourseLearningTime,
  CourseListParams,
} from '@/shared/types';

// ─── Query key factory ─────────────────────────────────────────────────────

export const courseKeys = {
  all: ['f', 'courses'] as const,
  list: (params: CourseListParams) => [...courseKeys.all, 'list', params] as const,
  content: (courseId: string) => [...courseKeys.all, 'content', courseId] as const,
  enrolment: (courseId: string) => [...courseKeys.all, 'enrolment', courseId] as const,
  learningTime: (courseId: string) => [...courseKeys.all, 'learning-time', courseId] as const,
  categories: () => [...courseKeys.all, 'categories'] as const,
  tags: () => [...courseKeys.all, 'tags'] as const,
};

// ─── Queries ───────────────────────────────────────────────────────────────

/** Fetch paginated list of live courses with optional filters. */
export function useCourses(params: CourseListParams = {}) {
  return useQuery<PaginatedResponse<Course>>({
    queryKey: courseKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResponse<Course>>('/courses', params as Record<string, unknown>),
    placeholderData: keepPreviousData,
  });
}

/** Fetch all course categories. */
export function useCourseCategories() {
  return useQuery<PaginatedResponse<CourseCategory>>({
    queryKey: courseKeys.categories(),
    queryFn: () =>
      api.get<PaginatedResponse<CourseCategory>>('/courses/categories', { pageSize: 100 }),
    staleTime: 10 * 60_000,
  });
}

/** Fetch all course tags. */
export function useCourseTags() {
  return useQuery<PaginatedResponse<CourseTag>>({
    queryKey: courseKeys.tags(),
    queryFn: () => api.get<PaginatedResponse<CourseTag>>('/courses/tags', { pageSize: 100 }),
    staleTime: 10 * 60_000,
  });
}

/** Fetch a course with full content hierarchy (modules → topics → lessons → steps). */
export function useCourseWithContent(courseId: string | undefined) {
  return useQuery<CourseWithContent>({
    queryKey: courseKeys.content(courseId!),
    queryFn: () => api.get<CourseWithContent>(`/courses/${courseId}`, { include: 'content' }),
    enabled: !!courseId,
  });
}

/** Check if the current user is enrolled in a course. */
export function useCourseEnrolment(courseId: string | undefined) {
  return useQuery<Enrolment | null>({
    queryKey: courseKeys.enrolment(courseId!),
    queryFn: () => api.get<Enrolment | null>(`/me/enrolments/${courseId}`),
    enabled: !!courseId,
    staleTime: 5 * 60_000,
  });
}

/** Fetch learning time data for a specific course enrolment. */
export function useCourseLearningTime(courseId: string | undefined) {
  return useQuery<CourseLearningTime>({
    queryKey: courseKeys.learningTime(courseId!),
    queryFn: () => api.get<CourseLearningTime>(`/me/enrolments/${courseId}/learning-time`),
    enabled: !!courseId,
    staleTime: 5 * 60_000,
  });
}

/** Create a new course. */
export function useCreateCourse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCourseInput) => api.post<Course>('/courses', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courseKeys.all });
    },
  });
}

// ─── Publish / Unpublish mutations ──────────────────────────────────────────

/** Publish a course for the first time (snapshots current content as v1). */
export function useInitialPublish(courseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/courses/${courseId}/publish`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courseKeys.content(courseId) });
      queryClient.invalidateQueries({ queryKey: courseKeys.all });
    },
  });
}

/** Unpublish a published course. */
export function useUnpublishCourse(courseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/courses/${courseId}/unpublish`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courseKeys.content(courseId) });
      queryClient.invalidateQueries({ queryKey: courseKeys.all });
    },
  });
}

// ─── Category mutations ──────────────────────────────────────────────────────

/** Create a new course category. */
export function useCreateCourseCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; slug: string }) =>
      api.post<CourseCategory>('/courses/categories', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.categories() });
    },
  });
}

/** Update an existing course category. */
export function useUpdateCourseCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      categoryId,
      data,
    }: {
      categoryId: string;
      data: { name: string; slug: string };
    }) => api.put<CourseCategory>(`/courses/categories/${categoryId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.categories() });
    },
  });
}

/** Delete a course category. */
export function useDeleteCourseCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (categoryId: string) => api.del(`/courses/categories/${categoryId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.categories() });
    },
  });
}

// ─── Tag mutations ───────────────────────────────────────────────────────────

/** Create a new course tag. */
export function useCreateCourseTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; slug: string }) =>
      api.post<CourseTag>('/courses/tags', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.tags() });
    },
  });
}

/** Update an existing course tag. */
export function useUpdateCourseTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tagId, data }: { tagId: string; data: { name: string; slug: string } }) =>
      api.put<CourseTag>(`/courses/tags/${tagId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.tags() });
    },
  });
}

/** Delete a course tag. */
export function useDeleteCourseTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tagId: string) => api.del(`/courses/tags/${tagId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.tags() });
    },
  });
}
